<template>
	<Surface
		:class="[
			$style['grid-box'],
			{ [$style['grid-box--clickable']]: clickable || routeName }
		]"
		@click.native="handleClick"
	>
		<BaseIconButton
			v-if="actions && actions.length"
			:class="$style['grid-box__actions-btn']"
			@click.stop="$refs['dropdown'].toggle()"
		>
			<FontAwesomeIcon
				:icon="['fal', 'ellipsis-v']"
				size="lg"
			/>
		</BaseIconButton>
		<DropdownMenu
			v-if="actions && actions.length"
			ref="dropdown"
			:class="$style['grid-box__dropdown']"
		>
			<DropdownMenuList :list="actions" />
		</DropdownMenu>
		<slot name="header" />
		<div :class="$style['grid-box__fields']">
			<div
				v-for="(column, index) in columns"
				:key="index"
				:class="$style['grid-box__field']"
			>
				<slot
					:name="`field-${column.name}`"
					:item="item"
					:column="column"
					:value="getFieldValue(column)"
				>
					<small :class="$style['grid-box__field__label']">
						{{ column.tag || (translationPrefix ? $t(`${translationPrefix}${column.name}`) : column.name) }}
					</small>
					<span :class="$style['grid-box__field__value']">
						{{ getFieldDisplay(column) }}
					</span>
				</slot>
			</div>
		</div>
		<slot name="footer" />
	</Surface>
</template>

<script>
import Surface from '@/components/Essentials/Surface'
import DropdownMenu from '@/components/Essentials/DropdownMenu'
import DropdownMenuList from '@/components/Essentials/DropdownMenu/DropdownMenuList'

export default {
	name: 'BaseGridBox',
	components: {
		Surface,
		DropdownMenu,
		DropdownMenuList
	},
	props: {
		item: {
			type: Object,
			required: true
		},
		columns: {
			type: Array,
			default: () => []
		},
		translationPrefix: {
			type: String,
			default: ''
		},
		routeName: {
			type: String,
			default: null
		},
		routeIdField: {
			type: String,
			default: 'id'
		},
		clickable: {
			type: Boolean,
			default: false
		},
		actions: {
			type: Array,
			default: () => []
		}
	},
	methods: {
		getFieldValue (column) {
			const key = column.sortKey || column.name
			let value = this.item[key]
			if (value === undefined || value === null) {
				value = this.item[column.name]
			}
			return value
		},
		getFieldDisplay (column) {
			const value = this.getFieldValue(column)
			if (value === undefined || value === null) {
				return '-'
			}
			if (typeof value === 'object') {
				return value.name || value.title || JSON.stringify(value)
			}
			return value
		},
		handleClick () {
			if (this.routeName) {
				this.$router.push({
					name: this.routeName,
					params: { [this.routeIdField]: this.item.id }
				})
			}
		}
	}
}
</script>

<style lang="scss" module>
	.grid-box {
		display: flex;
		flex-flow: column;
		height: 100%;
		cursor: default;
		transition: background-color 0.15s ease;

		&--clickable {
			cursor: pointer;

			&:hover {
				background-color: getColor(hover-overlay);
			}
		}

		&__actions-btn {
			position: absolute;
			right: $gutter;
			top: $gutter;
			font-size: 16px;
			z-index: 3;
		}

		&__dropdown {
			z-index: 3;
			top: $gutter * 2;
			right: $gutter * 2;
			transform-origin: top right;
		}

		&__header {
			margin-bottom: $gutter;
		}

		&__title {
			font-size: 16px;
			font-weight: bold;
			margin: 0;
			padding: 0;
			line-height: 1.3;
		}

		&__subtitle {
			font-size: 13px;
			color: getColor(sub-text);
			margin-top: 2px;
			line-height: 1.3;
		}

		&__caption {
			color: getColor(caption);
			font-size: 11px;
			text-transform: uppercase;
			letter-spacing: 0.5px;
		}

		&__id {
			font-size: 13px;
			color: getColor(sub-text);
			margin-bottom: calc($gutter / 2);
		}

		&__image {
			width: 100%;
			height: 140px;
			border-radius: 5px;
			overflow: hidden;
			margin-bottom: $gutter;
			background-color: getColor(grey);
			border: 1px solid getColor(border-image);
			position: relative;

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
				transition: transform cubic-bezier(0.075, 0.82, 0.165, 1) 3s;
			}

			&:hover img {
				transform: scale(1.05);
			}
		}

		&__avatar {
			display: flex;
			justify-content: center;
			margin-bottom: $gutter;
		}

		&__price {
			font-weight: bold;
			font-size: 14px;

			&--danger {
				color: getColor(error);
			}

			&--large {
				font-size: 24px;
				line-height: 1;
			}
		}

		&__priority-dot {
			height: 12px;
			width: 12px;
			border-radius: 50%;
			display: inline-block;
			margin-right: 6px;

			&--high {
				background-color: getColor(danger);
			}

			&--medium {
				background-color: getColor(warning);
			}

			&--low {
				background-color: getColor(success);
			}
		}

		&__footer {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-top: auto;
			padding-top: $gutter;
		}

		&__icon {
			margin-right: 8px;
			font-size: 14px;

			&--danger {
				color: getColor(danger);
			}

			&--success {
				color: getColor(success);
			}
		}

		&__link {
			color: getColor(tertiary);
			text-decoration: none;
			font-weight: 600;
			transition: color 0.15s ease;

			&:hover {
				color: getColor(tertiary-hover);
				text-decoration: underline;
			}
		}

		&__fields {
			display: flex;
			flex-wrap: wrap;
			gap: $gutter;
		}

		&__field {
			display: flex;
			flex-direction: column;
			min-width: 0;
			flex: 1 1 calc(50% - #{$gutter});

			@include media-breakpoint-only(xs) {
				flex: 1 1 100%;
			}

			&__label {
				color: getColor(caption);
				font-size: 11px;
				margin-bottom: 2px;
				text-transform: uppercase;
				letter-spacing: 0.5px;
			}

			&__value {
				font-size: 14px;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}
		}
	}
</style>
